import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, Avatar, Chip, Tooltip } from '@mui/material';
import * as MyCollections from "../services/MyCollections";
import { useAuthStore } from '../store/authStore';
import * as CommonFunctions from '../services/CommonFunctions';
import CustomTooltip from '../components/CustomTooltip';
import CircleIcon from '@mui/icons-material/Circle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import { urlAdditionalCosts } from '../config';
import useMediaQuery from '@mui/material/useMediaQuery';
import Controls from '../components/controls/Controls';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import ApproveAdditionalCosts from '../components/ApproveAdditionalCostsDialog';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useThemeContext } from '../components/contexts/ThemeContext';
import UploadCorrectionDialog from '../components/UploadCorrectionDialog';
import { CustomAlert } from '../components/CustomAlert';
import api from '../security/AxiosConfig';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))

const NameAvatarRenderer = (props) => {
  const accountantName = props.value;
  if (!accountantName || accountantName.trim() === '') {
    return <span style={{ color: props.context?.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>;
  }
  const parts = accountantName.split(' ');
  const surname = parts[0] || '';
  const name = parts.length > 1 ? parts[1] : '';
  const initials = `${surname.charAt(0)}${name.charAt(0)}`.toUpperCase();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context?.darkTheme ? '#10b981' : '#059669',
          color: '#ffffff',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: 'normal' }}>{accountantName}</span>
    </div>
  );
};

const ClientAvatarRenderer = (props) => {
  const clientName = props.value;
  if (!clientName || clientName.trim() === '') {
    return <span style={{ color: props.context?.darkTheme ? '#94a3b8' : '#64748b' }}>brak</span>;
  }
  const initials = clientName.substring(0, 2).toUpperCase();

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context?.darkTheme ? '#3b82f6' : '#2563eb', // Blue for clients
          color: '#ffffff',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: 'normal' }}>{clientName}</span>
    </div>
  );
};

function AdditionalCosts() {
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const userName = useAuthStore().fullName;
  const contactId = useAuthStore().contactId;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableData2, setTableData2] = useState([]);
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const gridRef = useRef();
  const [selectedYear, setSelectedYear] = useState(
    String(parseInt(CommonFunctions.getCurrentYear(), 10))
  );
  const [filteredData, setFilteredData] = useState(tableData);
  const isAccountantRole = roles?.includes('ACCOUNTANT') || roles?.includes('CHIEF_ACCOUNTANT') || roles?.includes('SUPERVISOR_ACCOUNTANT');
  const isApproveButtonAllowed = !isAccountantRole && (roles?.includes('MANAGER') || roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR'));
  const isExportButtonAllowed = !isAccountantRole && (roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR') || roles?.includes('ACCOUNT_MANAGER') || roles?.includes('MANAGER'));
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [currentDate, setCurrentDate] = useState(CommonFunctions.getCurrentDate())
  const [currentRecordForApproval, setCurrentRecordForApproval] = useState([]);
  const [isAdditionalWork, setIsAdditionalWork] = useState(true);
  const [value, setValue] = React.useState('one');

  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedSettlementId, setSelectedSettlementId] = useState(null);

  const handleOpenUploadDialog = useCallback((settlementId) => {
    if (!settlementId) {
      CustomAlert("Rozliczenie nie zostało powiązane z tymi pracami dodatkowymi.", "Błąd", "error");
      return;
    }
    setSelectedSettlementId(settlementId);
    setOpenUploadDialog(true);
  }, []);

  const handleUploadCorrection = useCallback((file) => {
    setOpenUploadDialog(false);
    CommonFunctions.handleFileSubmit(
      file,
      selectedSettlementId,
      71, // fixedFileType is 71 (Correction of additional work / additional costs)
      MyCollections.fileAssignmentId.ACCOUNTING_SETTLEMENT,
      token
    ).then(response => {
      if (response && response.success) {
        CustomAlert("Faktura korygująca została pomyślnie wgrana.", "Sukces", "success");
        const record = tableData.find(row => row.accountingSettlementId === selectedSettlementId);
        const targetAccountantName = record ? record.accountantName : `Rozliczenie ID: ${selectedSettlementId}`;
        const createdOn = record ? record.createdOn : null;
        const periodStr = createdOn ? `${String(new Date(createdOn).getMonth() + 1).padStart(2, '0')}.${new Date(createdOn).getFullYear()}` : '';
        const monthVal = createdOn ? new Date(createdOn).getMonth() + 1 : null;
        const yearVal = createdOn ? new Date(createdOn).getFullYear() : null;
        logActivity(
          `${logActionType.ADDITIONAL_WORK_CORRECTION_UPLOAD}${targetAccountantName}, okres: ${periodStr}, plik: ${file.name}`,
          userName,
          token,
          record ? Number(record.accountantId) : null,
          monthVal,
          yearVal,
          true
        );
        getAdditionalWork(); // refresh danych z backendu – isCorrectionUploaded będzie true
      }
    });
  }, [selectedSettlementId, token, tableData, userName]);
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "Księgowa/y",
      field: "accountantName",
      sortable: true,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 180,
      cellRenderer: NameAvatarRenderer,
      valueGetter: (params) => params.data.accountantName,
    },
    {
      headerName: "NIP",
      field: "accountantNIP",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      editable: !isAccountantRole,
      hide: isAccountantRole,
      minWidth: 125,
      valueGetter: (params) => {
        const nip = params.data?.AccountantNIP;
        return nip && nip.trim() !== '' ? nip : 'brak...';
      },
      cellStyle: {
        fontWeight: "bold",
        color: '#a668a5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      },
    },
    {
      headerName: "Kwota na fakturze",
      field: "additionalWorkAmount",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 125,
      cellStyle: (params) => {
        const amount = params.value;
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };
        if (amount > 0) {
          return { ...baseStyle, color: darkTheme ? "#87f385" : "green" };
        } else if (amount < 0) {
          return { ...baseStyle, color: "#ff4d4f" };
        } else {
          return { ...baseStyle, color: darkTheme ? "white" : "#000" }; // czarny dla zera
        }
      },
      valueFormatter: function (params) {
        const additionalWorkAmount = params.data.additionalWorkAmount || 0;
        const isVATPayer = params.data.isVATPayer;
        const isExcludedFromVAT = params.data.additionalWorkTypeId === 11;

        let amount = additionalWorkAmount;
        let label = 'netto';

        if (isVATPayer && !isExcludedFromVAT) {
          amount = additionalWorkAmount * 1.23;
          label = 'brutto';
        }

        return amount !== 0
          ? `${new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount)} ${label}`
          : "";
      }
    },
    {
      headerName: "Kto zatwierdził",
      field: "additionalWorkApproverName",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 125,
      valueGetter: (params) => {
        return params.data?.additionalWorkApproverName ?? "niezatwierdzone";
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };

        return params.value === "niezatwierdzone"
          ? { ...baseStyle, color: darkTheme ? '#a668a5' : '#9c27b0' }

          : baseStyle;
      }
    },
    {
      headerName: "Data utworzenia",
      field: "createdOn",
      lockPosition: 'left',
      filter: 'agDateColumnFilter',
      minWidth: 125,
      valueGetter: (params) => {
        return params.data?.createdOn;
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };

        return baseStyle;
      }
    },
    {
      headerName: "Data zatwierdzenia",
      field: "additionalWorkApprovalDate",
      lockPosition: 'left',
      filter: 'agDateColumnFilter',
      minWidth: 125,
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (!params.data?.additionalWorkApprovalDate && isApproveButtonAllowed) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button
                className="table-action-btn"
                onClick={() => handleOpenApproveDialog(params)}
              >
                <i className="fas fa-check"></i>
                Zatwierdź
              </button>
            </div>
          );
        } else {
          return (
            params.data?.additionalWorkApprovalDate
          )
        }
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: darkTheme ? '#66ff66' : "green",
        };

        return baseStyle;
      }
    },
    {
      headerName: "Opis prac",
      field: "additionalWorkDescription",
      filter: 'agTextColumnFilter',
      minWidth: 180,
      lockPosition: 'left',
      cellStyle: {
        display: 'flex',
        lineHeight: '1.5',
        textAlign: 'left',
        alignItems: 'center',
        whiteSpace: 'normal',
        overflow: 'hidden',
      },
      valueFormatter: (params) => {
        if (params.value) {
          const maxLength = 30;
          return params.value.length > maxLength
            ? params.value.substring(0, maxLength) + "..."
            : params.value;
        }
        return 'brak...';
      },
      tooltipValueGetter: (params) => params.value || '', // Pełny tekst jako tooltip
    },
    {
      headerName: "Kwota zatwierdzona",
      field: "additionalWorkApprovedAmount",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 125,
      cellStyle: (params) => {
        const amount = params.value;
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };
        if (amount > 0) {
          return { ...baseStyle, color: darkTheme ? "#87f385" : "green" };
        } else if (amount < 0) {
          return { ...baseStyle, color: "#ff4d4f" };
        } else {
          return { ...baseStyle, color: darkTheme ? "white" : "#000" }; // czarny dla zera
        }
      },
      valueFormatter: function (params) {
        if (!params.data || !params.data.additionalWorkApprovalDate) {
          return "";
        }
        const additionalWorkApprovedAmount = params.data.additionalWorkApprovedAmount || 0;
        const isVATPayer = params.data.isVATPayer;
        const isExcludedFromVAT = params.data.additionalWorkTypeId === 11;

        let amount = additionalWorkApprovedAmount;
        let label = 'netto';

        if (isVATPayer && !isExcludedFromVAT) {
          amount = additionalWorkApprovedAmount * 1.23;
          label = 'brutto';
        }

        return `${new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount)} ${label}`;
      }
    },
    {
      headerName: "Uwagi do prac dodatkowych",
      field: "additionalWorkComment",
      lockPosition: 'left',
      minWidth: 170,
      cellStyle: {
        display: 'flex',
        lineHeight: '1.5',
        textAlign: 'left',
        alignItems: 'center',
        whiteSpace: 'normal',
        overflow: 'hidden',
      },
      valueFormatter: (params) => {
        if (params.value) {
          const maxLength = 40;
          return params.value.length > maxLength
            ? params.value.substring(0, maxLength) + "..."
            : params.value;
        }
        return '';
      },
      tooltipValueGetter: (params) => params.value || '',
    },
    {
      headerName: "Akcja",
      width: 150,
      minWidth: 150,
      maxWidth: 160,
      sortable: false,
      filter: false,
      floatingFilter: false,
      hide: !isAccountantRole,
      cellRenderer: (params) => {
        if (!isAccountantRole) return null;
        if (!params.data?.isCorrectionRequired) return null;

        const correctionUploaded = params.data?.isCorrectionUploaded;

        if (correctionUploaded) {
          return (
            <div style={{ width: '100%', padding: '0 4px' }}>
              <Button
                variant="contained"
                size="small"
                disabled
                title="Korekta została już wgrana"
                sx={{
                  backgroundColor: darkTheme ? 'rgba(34, 197, 94, 0.1)' : 'rgba(22, 163, 74, 0.05)',
                  color: darkTheme ? '#4ade80' : '#16a34a',
                  opacity: 0.7,
                  boxShadow: 'none',
                  textTransform: 'none',
                  borderRadius: '20px',
                  fontWeight: '600',
                  padding: '4px 14px',
                  width: '100%',
                }}
              >
                ✓ Wgrano korektę
              </Button>
            </div>
          );
        }

        return (
          <div style={{ width: '100%', padding: '0 4px' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleOpenUploadDialog(params.data?.accountingSettlementId)}
              title="Wgraj fakturę korygującą"
              sx={{
                backgroundColor: darkTheme ? 'rgba(245, 158, 11, 0.15)' : 'rgba(217, 119, 6, 0.1)',
                color: darkTheme ? '#fbbf24' : '#d97706',
                boxShadow: 'none',
                textTransform: 'none',
                borderRadius: '20px',
                fontWeight: '600',
                padding: '4px 14px',
                width: '100%',
                '&:hover': {
                  backgroundColor: darkTheme ? 'rgba(245, 158, 11, 0.25)' : 'rgba(217, 119, 6, 0.2)',
                  boxShadow: 'none',
                }
              }}
            >
              Wgraj korektę
            </Button>
          </div>
        );
      },
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' }
    },
  ], [darkTheme, isAccountantRole, handleOpenUploadDialog]);

  const others = useMemo(() => [
    {
      headerName: "Klient",
      field: "clientName",
      sortable: true,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 180,
      cellRenderer: ClientAvatarRenderer,
      valueGetter: (params) => params.data.clientName,
      tooltipValueGetter: params => params.value || '',
    },
    {
      headerName: "NIP",
      field: "clientNIP",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      editable: !isAccountantRole,
      minWidth: 125,
      valueGetter: (params) => {
        const nip = params.data?.clientNIP;
        return nip && nip.trim() !== '' ? nip : 'brak...';
      },
      cellStyle: {
        fontWeight: "bold",
        color: darkTheme ? '#a668a5' : '#9c27b0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      },
    },
    {
      headerName: "Kwota",
      field: "discountAmount",
      lockPosition: "left",
      filter: "agTextColumnFilter",
      minWidth: 125,

      cellStyle: (params) => {
        const type = params.data?.type;

        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          fontWeight: 500
        };

        if (type === "DISCOUNT") {
          return { ...baseStyle, color: darkTheme ? "#87f385" : "green" };
        }

        if (type === "SURCHARGE") {
          return { ...baseStyle, color: "#ff4d4f" };
        }

        return { ...baseStyle, color: darkTheme ? "white" : "#000" };
      },

      valueGetter: (params) => {
        const amount = params.data?.discountAmount || 0;

        if (!amount) return "";

        return new Intl.NumberFormat("pl-PL", {
          style: "currency",
          currency: "PLN"
        }).format(amount);
      }
    },
    {
      headerName: "Typ",
      field: "type",
      lockPosition: "left",
      filter: "agTextColumnFilter",
      minWidth: 125,

      cellStyle: (params) => {
        const type = params.data?.type;

        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          fontWeight: 600
        };

        if (type === "DISCOUNT") {
          return { ...baseStyle, color: darkTheme ? "#87f385" : "green" };
        }

        if (type === "SURCHARGE") {
          return { ...baseStyle, color: "#ff4d4f" };
        }

        return baseStyle;
      },

      valueGetter: (params) => {
        const type = params.data?.type;

        if (type === "DISCOUNT") return "Rabat";
        if (type === "SURCHARGE") return "Dopłata";

        return "";
      }
    },
    {
      headerName: "Kto przydzielił",
      field: "assignedByName",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      editable: false,
      minWidth: 125,
      cellStyle: {
        color: darkTheme ? '#a668a5' : '#9c27b0',
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center"
      },
    },
    {
      headerName: "Kto zatwierdził",
      field: "approverName",
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 125,
      editable: false,
      valueGetter: (params) => {
        return params.data?.approverName ?? "niezatwierdzone";
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };

        return params.value === "niezatwierdzone"
          ? { ...baseStyle, color: darkTheme ? '#a668a5' : '#9c27b0' }
          : baseStyle;
      }
    },
    {
      headerName: "Data utworzenia",
      field: "createdOn",
      lockPosition: 'left',
      filter: 'agDateColumnFilter',
      minWidth: 125,
      valueGetter: (params) => {
        return params.data?.createdOn;
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        };

        return baseStyle;
      }
    },
    {
      headerName: "Data zatwierdzenia",
      field: "approvalDate",
      lockPosition: 'left',
      filter: 'agDateColumnFilter',
      minWidth: 125,
      cellRenderer: function (params) {
        if (!params.data) return null;
        if (isApproveButtonAllowed && params.data?.statusId === 11) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button
                className="table-action-btn"
                onClick={() => handleOpenApproveDialog(params)}
              >
                <i className="fas fa-check"></i>
                Zatwierdź
              </button>
            </div>
          );
        } else {
          return (
            params.data?.approvalDate
          )
        }
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: darkTheme ? "#87f385 " : "green"
        };

        return baseStyle;
      }
    },
    {
      headerName: "Komentarz wewnętrzny 7PM",
      field: "internalComment",
      filter: 'agTextColumnFilter',
      minWidth: 250,
      lockPosition: 'left',
      headerClass: 'no-separator',
      cellStyle: {
        display: 'flex',
        lineHeight: '1.5',
        textAlign: 'left',
        alignItems: 'center',
        whiteSpace: 'normal',
        overflow: 'hidden',
      },
      valueFormatter: (params) => {
        if (params.value) {
          const maxLength = 55;
          return params.value.length > maxLength
            ? params.value.substring(0, maxLength) + "..."
            : params.value;
        }
        return 'brak...';
      },
      tooltipValueGetter: (params) => params.value || '', // Pełny tekst jako tooltip
    }
  ], [darkTheme, isAccountantRole]);

  const defaultColDef = {
    resizable: true,
    editable: false,
    filter: false,
    flex: 1,
    tooltipComponent: CustomTooltip,
  }

  useEffect(() => {
    getAdditionalWork();
  }, []);

  useEffect(() => {
    let sourceData = isAdditionalWork ? tableData : tableData2;

    if (isAccountantRole && contactId) {
      sourceData = sourceData.filter(row => Number(row.accountantId) === Number(contactId));
    }

    if (selectedYear === 0) {
      setFilteredData(sourceData); // Pokaż wszystkie dane, jeśli nie wybrano filtra
    } else {
      const filtered = sourceData.filter(row => {
        const year = new Date(row.createdOn).getFullYear();
        return year == selectedYear;
      });
      setFilteredData(filtered);
    }
  }, [selectedYear, tableData, tableData2, isAdditionalWork, isAccountantRole, contactId]);

  const getAdditionalWork = () => {
    fetch(urlAdditionalCosts + "/getAllAdditionalWorkRecords", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        setTableData(data);
      })
      .catch(error => {
        console.error('Błąd pobierania danych:', error);
      });
  };

  const getDiscounts = () => {
    fetch(urlAdditionalCosts + "/getAllDiscountRecords", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        setTableData2(data);
      })
      .catch(error => {
        console.error('Błąd pobierania danych:', error);
      });
  };

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const onBtnExport = useCallback(() => {
    let columnsToExport;
    if (isAdditionalWork) {
      columnsToExport = [
        "accountantName",
        "accountantNIP",
        "additionalWorkAmount",
        "additionalWorkApproverName",
        "createdOn",
        "additionalWorkApprovalDate",
        "additionalWorkDescription",
        "additionalWorkApprovedAmount"
      ];
    } else {
      columnsToExport = [
        "clientName",
        "clientNIP",
        "discountAmount",
        "assignedByName",
        "createdOn",
        "approvalDate",
        "internalComment"
      ];
    }

    gridRef.current.api.exportDataAsCsv({
      columnKeys: columnsToExport,
    });
  }, [isAdditionalWork]);

  const clearFilters = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (api) {
      api.setFilterModel(null);

      if (api.applyColumnState) {
        api.applyColumnState({ defaultState: { sort: null } });
      } else if (columnApi && columnApi.applyColumnState) {
        columnApi.applyColumnState({ defaultState: { sort: null } });
      } else if (api.setSortModel) {
        api.setSortModel(null);
      }
    }
  }, []);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleOpenApproveDialog = async (params) => {
    setCurrentRecordForApproval(params.data);
    setOpenApproveDialog(true);
  }

  const handleCloseApproveDialog = async () => {
    setOpenApproveDialog(false);
  }

  const handleConfirmApproval = async (approvedAmount, isApproved = true, comment = '') => {
    if (isAdditionalWork) {
      currentRecordForApproval.additionalWorkApprovalDate = CommonFunctions.convertDateToJavaFormat(currentDate);
      currentRecordForApproval.additionalWorkApprovedBy = contactId;
      currentRecordForApproval.isAdditionalWorkApproved = isApproved;
      currentRecordForApproval.additionalWorkApprovedAmount = isApproved ? approvedAmount : 0;
      currentRecordForApproval.additionalWorkComment = comment;
      updateAdditionalWork(urlAdditionalCosts, currentRecordForApproval.id, currentRecordForApproval, currentDate);
      logActivity((isApproved ? logActionType.APPROVE_ADDITIONAL_WORK : "Odrzucono prace dodatkowe dla ") + currentRecordForApproval.accountantName + ", data: " + currentDate, userName, token);
    } else {
      currentRecordForApproval.approvalDate = CommonFunctions.convertDateToJavaFormat(currentDate);
      currentRecordForApproval.approvedById = contactId;
      currentRecordForApproval.statusId = 1;
      updateDiscount(urlAdditionalCosts, currentRecordForApproval.id, currentRecordForApproval, currentDate);
      logActivity(logActionType.APPROVE_DISCOUNT + currentRecordForApproval.clientName + ", data zatwierdzenia: " + currentDate, userName, token);
    }
    setOpenApproveDialog(false);
  };


  const updateAdditionalWork = (url, id, dataToSend) => {
    fetch(url + `/additionalWork/${id}`, {
      method: "PUT",
      body: JSON.stringify(dataToSend),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }).then(resp => resp.json())
      .then(resp => {
        getAdditionalWork();
      })
      .catch(error => {
        console.error('Błąd podczas wysyłania rekordu accountingSettlement:', error);
      });
  }

  const updateDiscount = (url, id, dataToSend) => {
    fetch(url + `/discount/${id}`, {
      method: "PUT",
      body: JSON.stringify(dataToSend),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }).then(resp => resp.json())
      .then(resp => {
        getAdditionalWork();
      })
      .catch(error => {
        console.error('Błąd podczas wysyłania rekordu accountingSettlement:', error);
      });
  }

  const handleChange = (e, value) => {
    setValue(value);
    if (value === "one") {
      getAdditionalWork()
      setIsAdditionalWork(true);
    }
    else {
      getDiscounts();
      setIsAdditionalWork(false);
    }
  };

  return (
    <>
      <Paper className={classes.pageContent} elevation={0}>
        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ px: 2, pt: 2, mb: 1 }}>
              {/* Zakładki + Dropdown po lewej */}
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {/* Zakładki */}
                <Tabs
                  value={value}
                  onChange={handleChange}
                  textColor="primary"
                  indicatorColor="primary"
                  aria-label="secondary tabs example"
                  sx={{
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#1976d2",
                      height: "3px",
                      borderRadius: "2px",
                      transition: "all 0.3s ease-in-out",
                    },
                  }}
                >
                  <Tab
                    value="one"
                    label="Prace dodatkowe"
                    sx={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: darkTheme ? "#fff" : "#555",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                        color: "#90caf9", // jasny niebieski pasujący do dark mode
                      },
                      "&.Mui-selected": {
                        color: darkTheme ? "#90caf9" : "#1976d2",
                        backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                      },
                    }}
                  />
                  {!isAccountantRole && (
                    <Tab
                      value="two"
                      label="Rabaty i dopłaty"
                      sx={{
                        fontSize: "14px",
                        textTransform: "none",
                        color: darkTheme ? "#fff" : "#555",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          backgroundColor: darkTheme ? "#333" : "#f0f0f0",
                          color: "#90caf9",
                        },
                        "&.Mui-selected": {
                          color: darkTheme ? "#90caf9" : "#1976d2",
                          backgroundColor: darkTheme ? "#1e1e1e" : "#e3f2fd",
                        },
                      }}
                    />
                  )}
                </Tabs>

                {/* Dropdown po zakładkach */}
                <Box minWidth={160}>
                  <Controls.MySelect
                    name="year"
                    label="Rok: "
                    value={selectedYear}
                    onChange={handleYearChange}
                    options={MyCollections.getYearCollection(2023, CommonFunctions.getCurrentYear())}
                  />
                </Box>
              </Box>

              {/* Przyciski po prawej */}
              <Box display="flex" justifyContent="flex-end" gap="15px" flexWrap="wrap">
                {isExportButtonAllowed && (
                  <Controls.PremiumButton
                    variant="glass"
                    label="CSV"
                    onClick={onBtnExport}
                    icon="fas fa-download"
                  />
                )}
                <Controls.PremiumButton
                  variant="glass"
                  label="Wyczyść"
                  onClick={clearFilters}
                  icon="fas fa-filter"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ m: '20px' }} />
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12}>
            <div className="App">
              <div className="tableData">
                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    rowData={filteredData}
                    columnDefs={isAdditionalWork ? columnDefs : others}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    ref={gridRef}
                    pagination={true}
                    paginationPageSize={200}
                    suppressBrowserResizeObserver={true}
                    animateRows={true}
                    rowSelection={false}
                    suppressSizeToFit={true}
                    enableBrowserTooltips={false}
                    localeText={MyCollections.localeTextPL}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    getRowHeight={() => 50}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <ApproveAdditionalCosts open={openApproveDialog} handleClose={handleCloseApproveDialog} isDiscount={!isAdditionalWork} handleConfirmApproval={handleConfirmApproval}
          contactName={isAdditionalWork ? currentRecordForApproval.accountantName : currentRecordForApproval.assignedByName} signature={userName} date={currentDate}
          amount={isAdditionalWork ? currentRecordForApproval.additionalWorkAmount : currentRecordForApproval.discountAmount}
          costDescription={isAdditionalWork ? currentRecordForApproval.additionalWorkDescription : currentRecordForApproval.invoiceDescription} darkTheme={darkTheme} gross={currentRecordForApproval.isVATPayer && currentRecordForApproval.additionalWorkTypeId !== 11} />
        <UploadCorrectionDialog
          open={openUploadDialog}
          handleClose={() => setOpenUploadDialog(false)}
          handleUpload={handleUploadCorrection}
          fixedFileType={71}
        />
      </Paper>
    </>
  );
}

export default AdditionalCosts;